// src/hooks/useAuth.js - REVISED AND CORRECTED
import React, { useState, useEffect, useContext, createContext } from 'react';
import { onAuthStateChanged, signOut, sendEmailVerification } from 'firebase/auth';
import { doc, onSnapshot } from 'firebase/firestore';
import { auth, db, functionUrl as cfUrl } from '../firebase';
import { isCreatingProfile } from '../utils/createProfile';

// Admin suspension. Legacy data uses `suspended: true` for both "not verified
// yet" (emailVerifiedAt null) and admin suspension (emailVerifiedAt set).
export const isAdminSuspended = (p) =>
    p.suspendedByAdmin === true || p.status === 'suspended' ||
    (p.suspended === true && !!p.emailVerifiedAt);

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
    // Consolidated State
    const [user, setUser] = useState(null);
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true); // Single loading state
    const [validationStatus, setValidationStatus] = useState('pending'); // 'pending', 'success', 'failed'
    const [modal, setModal] = useState(null);

    const setModalWithLog = (newModalState) => {
        console.log("[useAuth] setModal called. New state:", newModalState);
        setModal(newModalState);
    };

    const signOutUser = () => {
        console.log("[useAuth] signOutUser called.");
        setModalWithLog(null);
        signOut(auth).catch(err => console.error("[useAuth] Error signing out: ", err));
    };

    const activateUserProfile = async (userToActivate) => {
        try {
            console.log("[useAuth] activateUserProfile: Getting ID token...");
            const idToken = await userToActivate.getIdToken(true);
            const functionUrl = cfUrl("usersMarkEmailAsVerified");

            console.log("[useAuth] activateUserProfile: Calling Cloud Function..." );
            const response = await fetch(functionUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${idToken}`
                },
                body: JSON.stringify({ data: {} })
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error.message || 'Failed to activate profile.');
            }

            console.log("[useAuth] activateUserProfile: Profile activated successfully via Cloud Function.");
        } catch (error) {
            console.error("[useAuth] Error calling profile activation function:", error);
            setModalWithLog({ title: "Activation Error", message: `Could not finalize your account activation. ${error.message}` });
        }
    };

    // ✅ COMBINED AND CORRECTED useEffect
    useEffect(() => {
        let unsubscribeProfile = () => {}; // Initialize as an empty function

        const unsubscribeAuth = onAuthStateChanged(auth, async (authUser) => {
            console.log(`[useAuth] onAuthStateChanged fired. User: ${authUser?.uid || 'null'}`);
            
            // Clean up any previous profile listener
            unsubscribeProfile();

            if (authUser) {
                try {
                    // Wait for the ID token before the first Firestore read (avoids
                    // permission-denied right after sign-in). Not forced: a forced
                    // refresh changes the token, and Firestore then drops and
                    // reopens every listener.
                    await authUser.getIdToken();
                    console.log("[useAuth] ID Token is ready. Proceeding to attach Firestore listener.");

                    // Agora que o token está sincronizado, é seguro anexar o listener.
                    const profileRef = doc(db, 'users', authUser.uid);
                    
                    unsubscribeProfile = onSnapshot(profileRef, async (docSnapshot) => {
                        console.log("[useAuth] Firestore snapshot received.");
                        if (!docSnapshot.exists()) {
			    // ✨ CORREÇÃO DE RACE CONDITION ✨
                            // Verifica se o utilizador foi criado recentemente (últimos 30 segundos).
                            // Se sim, é provável que o perfil ainda esteja a ser criado pelo SignUpScreen.
                            // Simplesmente ignoramos e esperamos pela próxima atualização do snapshot.
                            const creationTime = authUser.metadata.creationTime ? new Date(authUser.metadata.creationTime).getTime() : 0;
                            const isNewUser = (Date.now() - creationTime) < 30000; // 30 segundos de tolerância

                            if (isNewUser && isCreatingProfile()) {
                                console.log("[useAuth] Profile is being created in this tab. Waiting...");
                                return; // O snapshot dispara de novo quando o doc for criado.
                            }
                            // Signed in (e.g. first Google login) but no profile yet:
                            // ask for terms / 18+ / CEP instead of failing.
                            console.log("[useAuth] No profile yet. Asking the user to complete signup.");
                            setUser(authUser);
                            setProfile(null);
                            setValidationStatus('needs_profile');
                            setLoading(false);
                            return;
                        }

                        const profileData = { id: docSnapshot.id, ...docSnapshot.data() };
                        const isProfileActivatedInDB = !!profileData.emailVerifiedAt;

                        if (isAdminSuspended(profileData)) {
                            setModalWithLog({
                                title: "Conta Suspensa",
                                message: "Sua conta está suspensa. Entre em contato com o suporte.",
                                actions: [{ text: 'Ok', onClick: signOutUser }]
                            });
                            setValidationStatus('failed');
                            setProfile(null);
                            setLoading(false);
                            return;
                        }

                        // Only a profile not yet activated needs the fresh e-mail
                        // status from Auth. Refreshing on every snapshot made each
                        // profile update restart all Firestore listeners.
                        let isEmailVerifiedInAuth = false;
                        if (!isProfileActivatedInDB) {
                            await authUser.reload();
                            const tokenResult = await authUser.getIdTokenResult(true);
                            isEmailVerifiedInAuth = tokenResult.claims.email_verified;
                        }

                        console.log(`[useAuth] Auth verification status: ${isEmailVerifiedInAuth}`);
                        console.log(`[useAuth] DB activation status: ${isProfileActivatedInDB}`);

                        if (isEmailVerifiedInAuth && !isProfileActivatedInDB) {
                            console.log("[useAuth] Condition met: Email verified, profile not activated. Calling activation function...");
                            await activateUserProfile(authUser);
                            return;
                        }

                        if (!isProfileActivatedInDB) {
                            console.error("[useAuth] Validation FAILED: Profile not activated in DB.");
                            const handleResendVerificationEmail = async () => {
                                try {
                                    await sendEmailVerification(authUser);
                                    setModalWithLog({
                                        title: "E-mail Enviado",
                                        message: "Um novo link de verificação foi enviado. Por favor, verifique seu e-mail e faça o login novamente.",
                                        actions: [{ text: 'Ok', onClick: signOutUser }]
                                    });
                                } catch (error) {
                                    console.error("Error resending verification email:", error);
                                    setModalWithLog({ title: "Erro", message: "Não foi possível reenviar o e-mail. Tente novamente mais tarde." });
                                }
                            };
                            setModalWithLog({
                                title: "Verificação de E-mail Necessária",
                                message: "Sua conta ainda não foi verificada. Por favor, verifique seu e-mail ou clique para reenviar o link de verificação.",
                                actions: [
                                    { text: 'Reenviar E-mail', onClick: handleResendVerificationEmail },
                                    { text: 'Sair', onClick: signOutUser }
                                ]
                            });
                            setValidationStatus('failed');
                            setProfile(null);
                        } else if (profileData.status === 'active') {
                            console.log("[useAuth] Validation SUCCESS: User is active and verified.");
                            setUser(authUser);
                            setProfile(profileData);
                            setValidationStatus('success');
                        } else {
                            console.error(`[useAuth] Validation FAILED: User status is '${profileData.status}'.`);
                            const message = profileData.status === 'suspended'
                                ? "Sua conta está suspensa. Entre em contato com o suporte."
                                : "Sua conta não está ativa. Entre em contato com o suporte.";
                            setModalWithLog({
                                title: "Conta Não Ativa",
                                message: message,
                                actions: [{ text: 'Ok', onClick: signOutUser }]
                            });
                            setValidationStatus('failed');
                            setProfile(null);
                        }
                        
                        setLoading(false);

                    }, (error) => {
                        console.error("[useAuth] CRITICAL: Error in Firestore snapshot listener.", error);
                        setModalWithLog({ title: "Access Error", message: "You do not have permission to access profile data.", actions: [{ text: 'Ok', onClick: signOutUser }] });
                        setValidationStatus('failed');
                        setProfile(null);
                        setLoading(false);
                    });
                } catch (error) {
                    console.error("[useAuth] Failed to get ID token or attach listener:", error);
                    signOutUser();
                }
            } else {
                // User is signed out. Clear everything.
                setUser(null);
                setProfile(null);
                setLoading(false);
                setValidationStatus('failed');
            }
        });

        // The main cleanup function for onAuthStateChanged.
        return () => {
            console.log("[useAuth] Cleaning up listeners.");
            unsubscribeAuth();
            unsubscribeProfile();
        };
    }, []); // Empty dependency array ensures this runs only once.

    const value = {
        user,
        profile,
        loading,
        validationStatus,
        modal,
        setModal: setModalWithLog,
        signOut: signOutUser,
    };

    console.log(`[useAuth] Provider rendering. Loading: ${loading}, Status: ${validationStatus}`);

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    return useContext(AuthContext);
};

